// Copyright ©️ 2026 eVolve MEP, LLC

import * as THREE from 'three';

import {
  BendPreviewGeometry,
  type BendPreviewSpecifications,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/BendPreviewGeometry';
import { convertToRadians, getTangentDistance } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import type { PlacementOffset } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/buildLabelOpts';

export class ThreePointSaddleGeometry extends BendPreviewGeometry {
  getDefaultDimensions() {
    // (takeUpLength - distanceToCenterOfObstruction) must exceed saddleHeight / tan(angle)
    return {
      ...super.getDefaultDimensions(),
      angle: 45,
      saddleHeight: 2,
      takeUpLength: 8,
      distanceToCenterOfObstruction: 4,
    };
  }

  createBendDefs(dimensions: BendPreviewSpecifications) {
    const { angle, saddleHeight, takeUpLength, distanceToCenterOfObstruction, bendRadius } = dimensions;
    const theta = convertToRadians(angle);

    // We want the saddle to go to the bottom of the conduit, not to the bend vertex.
    // So we add half the tangent distance of the full 2 * theta top bend to the hypotenuse.
    const hypotenuse = saddleHeight / Math.sin(theta) + getTangentDistance(bendRadius, 2 * theta) / 2;
    const saddleLegWidth = hypotenuse * Math.cos(theta);
    const startFlatLength = takeUpLength - distanceToCenterOfObstruction - saddleLegWidth;

    // Now we re-calculate the height to figure out how much we adjusted so we can shift the label down.
    const adjustedHeight = hypotenuse * Math.sin(theta);
    const adjustmentAmount = adjustedHeight - saddleHeight;

    // The saddle is adjusted to sit on the centerline using the value we calculated above,
    // Then shifted downward to sit at the bottom of the conduit.
    const saddleHeightLabelOffsets: PlacementOffset[] = [
      { relativeTo: 'world', offset: new THREE.Vector3(0, -adjustmentAmount, 0) },
      { relativeTo: 'world', offset: 'down' },
    ];

    return [
      {
        runLength: startFlatLength,
        thenBendBy: theta,
        startLabels: [
          {
            label: 'Take Up Length',
            length: takeUpLength,
            offsets: [{ relativeTo: 'world', offset: 'down' }],
          },
        ],
        endLabels: [{ label: 'Angle', angle, radius: Math.min(1, saddleLegWidth * 0.75) }],
      },
      {
        runLength: hypotenuse,
        thenRotateBy: Math.PI,
        thenBendBy: 2 * theta,
      },
      {
        runLength: hypotenuse,
        thenRotateBy: Math.PI,
        thenBendBy: theta,
        startLabels: [
          {
            label: 'Saddle Height',
            length: saddleHeight,
            isVertical: true,
            offsets: saddleHeightLabelOffsets,
          },
        ],
      },
      {
        runLength: distanceToCenterOfObstruction - saddleLegWidth,
        endLabels: [
          {
            label: 'Distance to Center of Obstruction',
            length: distanceToCenterOfObstruction,
            isReverse: true,
            directionPreference: 'inward',
            preferredDistance: 0.2,
            offsets: [
              {
                relativeTo: 'world',
                offset: 'down',
              },
            ],
          },
        ],
      },
    ] as const;
  }
}
